import type { SignerOption } from '@/api/signingApi'

/**
 * How a signature dialog offers "Sign as" (z8nrz7dt3z):
 *
 *  - `self`     — no picker; the signed-in user signs ("Apply Signature").
 *  - `optional` — defaults to the signed-in user, who may hand the signature
 *                 to a colleague (completion, version-control events).
 *  - `required` — the operator must pick who signs ("Sign as:", "Remove as:"),
 *                 exactly as the legacy context menu's dropdown did.
 */
export type SignerMode = 'self' | 'optional' | 'required'

export interface SignerChoice extends SignerOption {
  isSelf: boolean
}

/**
 * The dropdown rows, in the server's (alphabetical) order, with the signed-in
 * user marked. Ineligible people stay listed — seeing WHY someone cannot sign
 * before a password is typed is the point of listing them (AC2).
 */
export function signerChoices(options: readonly SignerOption[], selfId: string | null): SignerChoice[] {
  return options.map(o => ({ ...o, isSelf: selfId !== null && o.id === selfId }))
}

/** Who is selected when the dialog opens. */
export function initialSignerId(mode: SignerMode, selfId: string | null): string | null {
  return mode === 'required' ? null : selfId
}

/**
 * Whether the chosen signer may sign, and if not, why — for the line under the
 * picker and for disabling Confirm. The signed-in user missing from the roster
 * (a principal with no local account) is left to the server to judge rather
 * than blocked here, so this never refuses something the server would accept.
 */
export function signerBlocker(
  mode: SignerMode,
  chosenId: string | null,
  choices: readonly SignerChoice[],
  selfId: string | null,
): string | null {
  if (mode === 'self') return null
  if (chosenId === null) return mode === 'required' ? 'Choose who is signing.' : null
  const chosen = choices.find(c => c.id === chosenId)
  if (!chosen) return chosenId === selfId ? null : 'Choose who is signing.'
  return chosen.eligible ? null : `${chosen.name} cannot sign here: ${chosen.reason ?? 'not eligible'}.`
}

/** The chosen signer's display name, for "Password for …". */
export function signerName(
  chosenId: string | null,
  choices: readonly SignerChoice[],
  selfId: string | null,
  selfName: string,
): string {
  if (chosenId === null || chosenId === selfId) return selfName
  return choices.find(c => c.id === chosenId)?.name ?? selfName
}

/**
 * A process step that only its assignee may sign (start, undo-start, and a kit
 * run's end — the server's own rule) marks everyone else ineligible, so "Sign
 * as" offers the assignee rather than letting the operator pick someone the
 * server will refuse. Rows already ineligible keep their own reason.
 */
export function restrictToAssignee(
  options: readonly SignerOption[],
  assigneeId: string | null,
): SignerOption[] {
  return options.map(o =>
    !o.eligible || o.id === assigneeId
      ? o
      : { ...o, eligible: false, reason: 'Not assigned to this step' },
  )
}
